import { Duration } from 'luxon';

import { MINUTES_PER_DAY } from '../../../shared/consts/minutes-per-day';
import { ComputedPeriodDurationDto } from '../../../shared/modules/shared/dtos/computed-period-duration.dto';
import { PolicyApply } from '../interfaces/policy-apply.interface';
import { RentalDurationPolicyConfig } from '../interfaces/rental-duration-policy-config.interface';

/**
 * Applies rental duration policy to compute billable days from a duration.
 *
 * - No policy (`config` null/undefined): min 1 day via `Math.max(1, Math.ceil(totalDays))`.
 * - Policy with null/undefined/`<= 0`/or `>= 1440` threshold: `Math.round(totalDays)` (0 allowed).
 * - Policy with threshold in 1..1439: fullDays + 1 when fractional minutes meet threshold (0 allowed).
 */
export const RentalDurationPolicy: PolicyApply<
	Duration,
	ComputedPeriodDurationDto,
	RentalDurationPolicyConfig
> = {
	apply(
		duration: Duration,
		config: RentalDurationPolicyConfig | null | undefined,
	): ComputedPeriodDurationDto {
		const totalDays = duration.as('days');
		const fullDays = Math.floor(totalDays);
		const fractionalMinutes = (totalDays - fullDays) * MINUTES_PER_DAY;

		let billableDays: number;

		if (config == null) {
			billableDays = Math.max(1, Math.ceil(totalDays));
		} else {
			const threshold = config.dayThresholdMinutes;

			if (
				threshold === undefined ||
				threshold === null ||
				threshold <= 0 ||
				threshold >= MINUTES_PER_DAY
			) {
				billableDays = Math.round(totalDays);
			} else {
				billableDays =
					fullDays + (fractionalMinutes >= threshold ? 1 : 0);
			}
		}

		return {
			years: 0,
			quarters: 0,
			months: 0,
			weeks: 0,
			days: billableDays,
			hours: 0,
			minutes: 0,
			seconds: 0,
			milliseconds: 0,
		};
	},
};
